// src/components/ClubPage.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HamburgerMenu from "./HamburgerMenu";
import PageHeader from "./PageHeader";
import UserLink from "./UserLink";
import DiveLoader from "./DiveLoader";
import { fetchWithAuth } from "../utils/api";
import { supabase } from "../supabaseClient";
import "./Profile.css";

const CLUB_COLORS = ["#0077b6", "#0096c7", "#2e9e5b", "#e0a400", "#8a6d3b", "#6f4a9e", "#c0507a"];
const clubColor = (name) => {
    let h = 0;
    for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return CLUB_COLORS[h % CLUB_COLORS.length];
};

export default function ClubPage({ currentUser, onLogout }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const apiUrl = process.env.REACT_APP_API_URL;

    const [club, setClub] = useState(undefined); // undefined = chargement, null = introuvable
    const [addingMember, setAddingMember] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [confirmDialog, setConfirmDialog] = useState(null); // { message, onConfirm }
    const [clubDives, setClubDives] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [newAnnounce, setNewAnnounce] = useState("");

    const loadClub = useCallback(async () => {
        try {
            const res = await fetchWithAuth(`${apiUrl}/clubs/${id}`);
            if (res.status === 404) { setClub(null); return; }
            setClub(await res.json());
        } catch {
            setClub(null);
        }
    }, [apiUrl, id]);

    useEffect(() => { loadClub(); }, [loadClub]);

    const loadClubDives = useCallback(() => {
        fetchWithAuth(`${apiUrl}/clubs/${id}/dives`)
            .then((r) => (r.ok ? r.json() : []))
            .then((d) => setClubDives(Array.isArray(d) ? d : []))
            .catch(() => {});
    }, [apiUrl, id]);
    useEffect(() => { loadClubDives(); }, [loadClubDives, club]);

    const handleJoinClubDive = async (diveId) => {
        await fetchWithAuth(`${apiUrl}/join-dive`, { method: "POST", body: JSON.stringify({ dive_intention_id: diveId }) });
        loadClubDives();
    };

    const loadAnnouncements = useCallback(() => {
        fetchWithAuth(`${apiUrl}/clubs/${id}/announcements`)
            .then((r) => (r.ok ? r.json() : []))
            .then((d) => setAnnouncements(Array.isArray(d) ? d : []))
            .catch(() => {});
    }, [apiUrl, id]);
    useEffect(() => { loadAnnouncements(); }, [loadAnnouncements, club]);

    const postAnnouncement = async () => {
        if (!newAnnounce.trim()) return;
        await fetchWithAuth(`${apiUrl}/clubs/${id}/announcements`, { method: "POST", body: JSON.stringify({ content: newAnnounce.trim() }) });
        setNewAnnounce("");
        loadAnnouncements();
    };
    const deleteAnnouncement = async (annId) => {
        await fetchWithAuth(`${apiUrl}/clubs/${id}/announcements/${annId}`, { method: "DELETE" });
        loadAnnouncements();
    };

    const amAdmin = club?.myMembership?.role === "admin" && club?.myMembership?.status === "approved";
    const amMember = club?.myMembership?.status === "approved";
    const amPending = club?.myMembership?.status === "pending";

    const post = async (path, body) => {
        await fetchWithAuth(`${apiUrl}${path}`, { method: "POST", body: body ? JSON.stringify(body) : undefined });
        loadClub();
    };

    const handleJoin = () => post(`/clubs/${id}/join`);
    const handleApprove = (uid) => post(`/clubs/${id}/members/${uid}/approve`);
    const handleRemove = (uid) => post(`/clubs/${id}/members/${uid}/remove`);
    const handleAdd = (uid) => post(`/clubs/${id}/add-member`, { user_id: uid });
    const handleRole = (uid, role) => post(`/clubs/${id}/members/${uid}/role`, { role });
    const handleLeave = () => setConfirmDialog({
        message: t("clubs.confirmLeave"),
        onConfirm: async () => {
            await fetchWithAuth(`${apiUrl}/clubs/${id}/leave`, { method: "POST" });
            navigate("/dives");
        },
    });
    const handleDelete = () => setConfirmDialog({
        message: t("clubs.confirmDelete"),
        onConfirm: async () => {
            await fetchWithAuth(`${apiUrl}/clubs/${id}`, { method: "DELETE" });
            navigate("/dives");
        },
    });

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const ext = file.name.split(".").pop();
        const fileName = `club_${id}_${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("profile-pictures").upload(fileName, file);
        if (upErr) return;
        const { data: pub } = supabase.storage.from("profile-pictures").getPublicUrl(fileName);
        await fetchWithAuth(`${apiUrl}/clubs/${id}/logo`, { method: "POST", body: JSON.stringify({ logo_url: pub?.publicUrl }) });
        loadClub();
    };

    const openAddMember = () => {
        setAddingMember(true);
        setUserSearch("");
        if (allUsers.length === 0) {
            fetchWithAuth(`${apiUrl}/users`).then((r) => (r.ok ? r.json() : [])).then((d) => setAllUsers(Array.isArray(d) ? d : [])).catch(() => {});
        }
    };

    const memberIds = useMemo(() => new Set((club?.members || []).map((m) => m.id).concat((club?.pending || []).map((p) => p.id))), [club]);
    const addable = useMemo(() => {
        const term = userSearch.trim().toLowerCase();
        return allUsers
            .filter((u) => !memberIds.has(u.id))
            .filter((u) => { const n = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase(); return !term || n.includes(term); })
            .slice(0, 30);
    }, [allUsers, memberIds, userSearch]);

    const containerStyle = {
        width: "90%", maxWidth: "900px", margin: "40px auto", padding: "20px",
        backgroundColor: "#f9f9f9", borderRadius: "12px",
        boxShadow: "0 4px 10px rgba(0,0,0,0.1)", position: "relative",
    };

    if (club === undefined) {
        return <div className="profile-container" style={containerStyle}><DiveLoader /></div>;
    }
    if (club === null) {
        return (
            <div className="profile-container" style={containerStyle}>
                <p style={{ textAlign: "center", color: "#888" }}>{t("clubs.notFound")}</p>
            </div>
        );
    }

    const creatorName = (() => {
        const c = (club.members || []).find((m) => m.id === club.created_by);
        return c ? `${c.first_name || ""} ${c.last_name ? c.last_name.charAt(0) + "." : ""}`.trim() : null;
    })();

    return (
        <div className="profile-container" style={containerStyle}>
            {/* Retour */}
            <button
                type="button"
                onClick={() => navigate("/dives")}
                aria-label={t("common.close")}
                style={{ position: "absolute", top: 15, right: 52, zIndex: 1500, background: "#fff", border: "1px solid #e0e0e0", borderRadius: "50%", width: 34, height: 34, cursor: "pointer", color: "#555", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div style={{ position: "absolute", top: 15, right: 10, zIndex: 1500 }}>
                <HamburgerMenu onLogout={onLogout} onProfileClick={() => navigate("/profile")} onAboutClick={() => navigate("/about")} onMapClick={() => navigate("/")} onCommunityClick={() => navigate("/community")} />
            </div>

            <div className="page-header-bleed">
                <PageHeader title={club.name} />
            </div>

            {/* Logo du club */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                {club.logo_url ? (
                    <img src={club.logo_url} alt="" style={{ width: 60, height: 60, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />
                ) : (
                    <span style={{ width: 60, height: 60, borderRadius: 12, flexShrink: 0, background: clubColor(club.name), color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 26, fontFamily: "var(--font-display)" }}>
                        {(club.name || "?").charAt(0).toUpperCase()}
                    </span>
                )}
                {amAdmin && (
                    <label style={{ color: "var(--primary-blue)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                        {t("clubs.changeLogo")}
                        <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
                    </label>
                )}
            </div>

            {creatorName && (() => {
                // On coupe la phrase traduite autour du nom pour rendre celui-ci cliquable (→ profil).
                const [before, after] = t("clubs.createdBy", { name: "@@N@@" }).split("@@N@@");
                return (
                    <p style={{ marginTop: 0, color: "#8a97a3", fontSize: 13 }}>
                        {before}<UserLink id={club.created_by} firstName={creatorName} />{after}
                    </p>
                );
            })()}
            {club.description && <p style={{ lineHeight: 1.6, color: "#444", whiteSpace: "pre-wrap" }}>{club.description}</p>}

            {/* Actions d'adhésion */}
            <div style={{ margin: "14px 0" }}>
                {!club.myMembership && (
                    <button onClick={handleJoin} style={primaryBtn}>{t("clubs.join")}</button>
                )}
                {amPending && (
                    <span style={{ color: "#e0a400", fontWeight: 600, fontSize: 14 }}>{t("clubs.requested")}</span>
                )}
                {amMember && !amAdmin && (
                    <button onClick={handleLeave} style={secondaryBtn}>{t("clubs.leave")}</button>
                )}
            </div>

            {/* Annonces (membres) */}
            {amMember && (
                <div className="profile-section">
                    <h3 style={{ color: "var(--primary-blue)" }}>{t("clubs.announcements")}</h3>
                    {amAdmin && (
                        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                            <input
                                type="text"
                                value={newAnnounce}
                                onChange={(e) => setNewAnnounce(e.target.value)}
                                placeholder={t("clubs.announcePlaceholder")}
                                style={{ flex: 1, padding: "9px 11px", border: "1px solid #ccd6dd", borderRadius: 9, fontSize: 14 }}
                                onKeyDown={(e) => { if (e.key === "Enter") postAnnouncement(); }}
                            />
                            <button onClick={postAnnouncement} style={smallPrimary}>{t("common.send")}</button>
                        </div>
                    )}
                    {announcements.length === 0 ? (
                        <p style={{ color: "#888" }}>{t("clubs.noAnnouncements")}</p>
                    ) : (
                        announcements.map((a) => (
                            <div key={a.id} style={{ background: "#fff", border: "1px solid #eef2f4", borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                    <span style={{ fontSize: 12, color: "#8a97a3" }}>
                                        {a.author?.first_name || "?"} · {new Date(a.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                                    </span>
                                    {amAdmin && (
                                        <button onClick={() => deleteAnnouncement(a.id)} style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: 12 }}>
                                            {t("clubs.remove")}
                                        </button>
                                    )}
                                </div>
                                <div style={{ color: "#334", marginTop: 4, whiteSpace: "pre-wrap" }}>{a.content}</div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Plongées du club à venir (membres) */}
            {amMember && (
                <div className="profile-section">
                    <h3 style={{ color: "var(--primary-blue)" }}>{t("clubs.upcomingDives")}</h3>
                    {clubDives.length === 0 ? (
                        <p style={{ color: "#888" }}>{t("clubs.noUpcoming")}</p>
                    ) : (
                        clubDives.map((d) => (
                            <div key={d.id} style={rowStyle}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, color: "#223344", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.site?.name || "?"}</div>
                                    <div style={{ fontSize: 13, color: "#667" }}>
                                        {new Date(d.planned_date).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                                        {d.planned_time ? ` · ${d.planned_time.slice(0, 5)}` : ""}
                                        {d.organizer?.first_name ? <> · <UserLink id={d.organizer.id} firstName={d.organizer.first_name} /></> : ""}
                                    </div>
                                </div>
                                {d.is_organizer ? (
                                    <span style={{ fontSize: 12, color: "#8a97a3", fontWeight: 700, flexShrink: 0 }}>{t("clubs.organizerTag")}</span>
                                ) : d.is_participant ? (
                                    <span style={{ fontSize: 12, color: "#2e9e5b", fontWeight: 700, flexShrink: 0 }}>{t("clubs.joinedTag")}</span>
                                ) : (
                                    <button onClick={() => handleJoinClubDive(d.id)} style={smallPrimary}>{t("map.buttons.join")}</button>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Demandes en attente (admin) */}
            {amAdmin && club.pending && club.pending.length > 0 && (
                <div className="profile-section">
                    <h3 style={{ color: "var(--primary-blue)" }}>{t("clubs.pending")}</h3>
                    {club.pending.map((u) => (
                        <div key={u.id} style={rowStyle}>
                            <div style={memberInfo}>
                                <img src={u.profile_image_url || "/default-avatar.png"} alt="" style={avatar} />
                                <span><UserLink id={u.id} firstName={u.first_name} lastName={u.last_name} /></span>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button onClick={() => handleApprove(u.id)} style={smallPrimary}>{t("clubs.approve")}</button>
                                <button onClick={() => handleRemove(u.id)} style={smallGhost}>{t("clubs.reject")}</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Membres */}
            <div className="profile-section">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3 style={{ color: "var(--primary-blue)", margin: 0 }}>{t("clubs.members")} ({club.members?.length || 0})</h3>
                    {amAdmin && !addingMember && (
                        <button onClick={openAddMember} style={smallPrimary}>+ {t("clubs.addMember")}</button>
                    )}
                </div>

                {/* Ajout de membre (admin) */}
                {amAdmin && addingMember && (
                    <div style={{ marginTop: 10, border: "1px solid #e2e8ec", borderRadius: 10, padding: 10, background: "#fff" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <input autoFocus type="text" placeholder={t("clubs.searchMember")} value={userSearch} onChange={(e) => setUserSearch(e.target.value)} style={{ flex: 1, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 8, fontSize: 14 }} />
                            <button onClick={() => setAddingMember(false)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", marginLeft: 8 }}>{t("common.cancel")}</button>
                        </div>
                        <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 200, overflowY: "auto" }}>
                            {addable.map((u) => (
                                <li key={u.id}>
                                    <button onClick={() => handleAdd(u.id)} style={{ width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: "1px solid #f0f0f0", padding: "9px 6px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                                        <img src={u.profile_image_url || "/default-avatar.png"} alt="" style={avatar} />
                                        <span>{u.first_name} {u.last_name}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div style={{ marginTop: 10 }}>
                    {(club.members || []).length === 0 ? (
                        <p style={{ color: "#888" }}>{t("clubs.noMembers")}</p>
                    ) : (
                        club.members.map((m) => (
                            <div key={m.id} style={rowStyle}>
                                <a href={`/profile/${m.id}`} style={{ ...memberInfo, textDecoration: "none", color: "#223344" }}>
                                    <img src={m.profile_image_url || "/default-avatar.png"} alt="" style={avatar} />
                                    <span>{m.first_name} {m.last_name}</span>
                                    {m.role === "admin" && <span style={adminBadge}>{t("clubs.admin")}</span>}
                                </a>
                                {amAdmin && m.id !== currentUser?.id && (
                                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                        <button
                                            onClick={() => handleRole(m.id, m.role === "admin" ? "member" : "admin")}
                                            title={m.role === "admin" ? t("clubs.demote") : t("clubs.promote")}
                                            aria-label={m.role === "admin" ? t("clubs.demote") : t("clubs.promote")}
                                            style={{ ...iconBtn, color: m.role === "admin" ? "#e0a400" : "#8aa0a8", borderColor: m.role === "admin" ? "#f2d68a" : "#d7dee2" }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill={m.role === "admin" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 7.1-1.01L12 2z" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => handleRemove(m.id)}
                                            title={t("clubs.remove")}
                                            aria-label={t("clubs.remove")}
                                            style={{ ...iconBtn, color: "#c0392b", borderColor: "#e5b8b2" }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Zone admin : supprimer le club */}
            {amAdmin && (
                <div style={{ marginTop: 20, textAlign: "center" }}>
                    <button onClick={handleDelete} style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                        {t("clubs.deleteClub")}
                    </button>
                </div>
            )}

            {/* Boîte de confirmation stylée */}
            {confirmDialog && (
                <div
                    onClick={() => setConfirmDialog(null)}
                    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10003, padding: 16 }}
                >
                    <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 360, padding: 22, boxShadow: "0 12px 40px rgba(0,0,0,0.25)", textAlign: "center" }}>
                        <p style={{ margin: "0 0 18px", color: "#223344", fontSize: 15, lineHeight: 1.5 }}>{confirmDialog.message}</p>
                        <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
                            <button onClick={() => setConfirmDialog(null)} style={{ background: "#eef2f4", color: "#445", border: "none", borderRadius: 10, padding: "9px 18px", fontWeight: 700, cursor: "pointer" }}>{t("common.cancel")}</button>
                            <button onClick={() => { const fn = confirmDialog.onConfirm; setConfirmDialog(null); fn(); }} style={{ background: "#c0392b", color: "#fff", border: "none", borderRadius: 10, padding: "9px 18px", fontWeight: 700, cursor: "pointer" }}>{t("clubs.confirmBtn")}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const primaryBtn = { background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 700, cursor: "pointer" };
const secondaryBtn = { background: "#fff", color: "var(--primary-blue)", border: "1px solid rgba(0,119,182,0.35)", borderRadius: 10, padding: "9px 16px", fontWeight: 700, cursor: "pointer" };
const smallPrimary = { background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer" };
const smallGhost = { background: "none", color: "#c0392b", border: "1px solid #e5b8b2", borderRadius: 8, padding: "6px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const iconBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", background: "#fff", border: "1px solid #d7dee2", cursor: "pointer", flexShrink: 0, padding: 0 };
const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #eef2f4", gap: 8 };
const memberInfo = { display: "flex", alignItems: "center", gap: 10, minWidth: 0 };
const avatar = { width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 };
const adminBadge = { fontSize: 11, fontWeight: 700, color: "#e0a400", background: "#fff6e0", borderRadius: 999, padding: "2px 8px" };
